/**
 * Promo tile placement, client side. Authoritative — runs on load and after every load more.
 *
 * Rows on a promo tile are absolute: row 1 is the first row of the collection, not of the
 * page. Every tile is rendered once, on the first page, and is shown at a breakpoint only
 * where its row exists. Load more adds rows rather than markup, so tiles waiting on those
 * rows appear when this pass re-runs. Tiles are never pulled back onto a row they were not
 * authored to — clamping shoved products out of place and moved the tile again as soon as
 * the real row arrived.
 *
 * "Where its row exists" is walked, not estimated. A tile spans two columns, so it steals
 * cells from its row and pushes the products after it further down; how far the grid
 * reaches is the row the last product lands on, which depends on the tiles above it.
 * Estimating instead (products plus two cells per tile) counts tiles that turn out to be
 * off the end, and leaves a tile sitting alone on a row with no products beside it.
 *
 * armada-promo-placement.liquid does a coarser version of this server side, so the first
 * paint and JS-off are close; this pass corrects it.
 */
(function () {
  'use strict';

  /* Column count per band, and the custom properties the matching media query reads.
     The lg-* bands are the large-tile counts (AAS-5634, .armada-grid--large); both
     sets are resolved up front so toggling the tile size needs no re-placement.
     lg-1col is the large mobile grid: its authored mobile row is against two
     columns, so it is doubled back to the same number of products above the tile
     (rowScale) instead of moving the tile up the page. */
  var BREAKPOINTS = [
    { columns: 2, name: 'mobile', mobileRow: true },
    { columns: 3, name: '3col' },
    { columns: 4, name: '4col' },
    { columns: 5, name: '5col' },
    { columns: 6, name: '6col' },
    { columns: 8, name: '8col' },
    { columns: 1, name: 'lg-1col', mobileRow: true, rowScale: 2 },
    { columns: 2, name: 'lg-2col' },
    { columns: 3, name: 'lg-3col' },
    { columns: 4, name: 'lg-4col' },
    { columns: 6, name: 'lg-6col' },
  ];

  var MAX_ATTEMPTS = 12;

  /**
   * The row the last product lands on, given which rows the tiles hold.
   * rowsHeld maps row number -> tiles on that row.
   */
  function lastProductRow(products, columns, rowsHeld) {
    var guard = products + Object.keys(rowsHeld).length * 2 + 5;
    var remaining = products;
    var row = 0;

    while (remaining > 0 && row < guard) {
      row++;
      var tilesHere = rowsHeld[row] || 0;
      /* A tile takes two tracks (the whole row at 1–2 columns). At 3 columns that
         still leaves one cell for a product, even though the row takes no second
         tile — counting it as full overstated the reach by a row and showed a
         later tile on a row with no products. */
      var free = Math.max(0, columns - tilesHere * 2);
      if (free > 0) remaining -= free;
    }

    return row;
  }

  function placePromoTiles(grid) {
    if (!grid) return;

    var tiles = grid.querySelectorAll(':scope > .armada-product-grid__super-tile');
    if (!tiles.length) return;

    var products = grid.querySelectorAll(
      ':scope > .armada-product-grid__item:not([data-regios-payload])'
    ).length;

    BREAKPOINTS.forEach(function (breakpoint) {
      var taken = Object.create(null);
      var rowsHeld = Object.create(null);
      var wanted = [];

      /* Resolve rows first: the authored row when free, else the nearest free row above,
         else below. Two tiles never share a cell, whatever the grid ends up reaching. */
      Array.prototype.forEach.call(tiles, function (tile) {
        var authored = parseInt(
          breakpoint.mobileRow ? tile.dataset.promoRowMobile : tile.dataset.promoRowDesktop,
          10
        ) || 1;
        if (breakpoint.rowScale) authored = (authored - 1) * breakpoint.rowScale + 1;
        var column = tile.dataset.promoColumn === '1' ? 1 : 3;
        var lane = breakpoint.columns < 4 ? 'full' : column;

        var row = authored;
        for (var offset = 1; offset <= MAX_ATTEMPTS; offset++) {
          if (!taken[row + '-' + lane]) break;
          row -= 1;
          if (row < 1) row = authored + offset;
        }

        taken[row + '-' + lane] = true;
        rowsHeld[row] = (rowsHeld[row] || 0) + 1;
        wanted.push({ tile: tile, row: row });
      });

      var reach = lastProductRow(products, breakpoint.columns, rowsHeld);

      wanted.forEach(function (entry) {
        if (entry.row > reach) {
          entry.tile.style.setProperty('--promo-display-' + breakpoint.name, 'none');
          entry.tile.style.removeProperty('--promo-row-' + breakpoint.name);
          return;
        }
        entry.tile.style.removeProperty('--promo-display-' + breakpoint.name);
        entry.tile.style.setProperty('--promo-row-' + breakpoint.name, entry.row);
      });
    });
  }

  function placeAll() {
    document.querySelectorAll('.armada-product-grid').forEach(placePromoTiles);
  }

  window.armadaPlacePromoTiles = placePromoTiles;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeAll);
  } else {
    placeAll();
  }
})();
